import tinycolor from 'tinycolor2';
import {
  sanitizeTrims,
  sanitizeVideoElements,
  sanitizeAudioElements,
  sanitizeImageElements,
  sanitizeCallOutElements,
  sanitizeScenes,
  sanitizeEffects,
  sanitizeScenesForSplit,
  sanitizeEffectsForSplit,
  sanitizeTrimsForSplit
} from './elementSanitizers';
import { getAllCompositeSegments, getClipFPS } from '@/player-config/src/utils/clipUtils';
import type { Clip } from '@/player-config/src/types/clip-types';
import type { AIEditsSplitConfig, AIEditsConfigWithSplits } from '@/adapters/aiEdits/types';
import { brandKit, getSelectedPlatformBrandKit } from '@/stores/brandKit';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import type { PlatformBrandKitColors } from '@/Pages/BrandKit/types';
import { getFontFullPath } from '@/player-config/src/utils/fontUtils';
import { LottieElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/LottieElement';
import {
  updateAllElementsWithAIEdits,
  updateAllElementsWithAIEditsAndSplits
} from '@/App/remotion/MainPlayer/utils/playerConfig/aiEdits/aiEditsUpdater';
import { trackClipUpdate } from '@/stores/clip';
import { getVideoSource } from '@/hooks/utils/analyticsHookUtil';
import { AudioElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/AudioElement';
import { ImageElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import { VideoElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { TextElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/TextElement';
import { Effect } from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Effects';
import { Scene } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { SanitizedTrim } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/types';

const transformAIEditsData = async (data: any, clipData: Clip) => {
  const { elements, effects = [], layouts = [] } = data.ai_edits;
  const { trims = [] } = data;
  const fps = getClipFPS(clipData);

  const allElements = elements ? await transformElementsFromAIEdits(elements, clipData) : undefined;
  const sanitizedScenes = sanitizeScenes(layouts || [], fps, clipData);
  const sanitizedEffects = sanitizeEffects(effects || [], fps, clipData);
  const sanitizedTrims = sanitizeTrims(trims || [], clipData);

  return {
    allElements,
    effects: sanitizedEffects,
    sanitizedScenes,
    sanitizedTrims
  };
};

export function getCaptionHighlightColor(): string {
  const usePlatformBrandKit = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Platform_Brandkit];

  let colors: string[] = [];

  if (usePlatformBrandKit) {
    const activeKit = getSelectedPlatformBrandKit();
    const kitColors = activeKit?.colors as PlatformBrandKitColors | undefined;
    colors = [...(kitColors?.primary ?? []), ...(kitColors?.secondary ?? []), ...(kitColors?.neutral ?? [])];
  } else {
    colors = brandKit.getSnapshot().colors || [];
  }

  const sortedColors = colors
    .filter(color => tinycolor(color).getBrightness() < 128)
    .sort((a, b) => {
      return tinycolor(b).toHsl().s - tinycolor(a).toHsl().s;
    });

  return sortedColors[0] || '#FF4200';
}

const buildEditTypes = (
  elements?: { videos?: any[]; images?: any[]; text?: any[]; music?: any[] },
  effects?: any[],
  layouts?: any[]
): string[] => {
  const editTypes: string[] = [];

  if (elements?.videos?.length) {
    editTypes.push('Video Overlay');
  }
  if (elements?.images?.length) {
    editTypes.push('Image Overlay');
  }
  if (elements?.text?.length) {
    editTypes.push('Text Overlay');
  }
  if (elements?.music?.length) {
    editTypes.push('Audio Overlay');
  }
  if (effects?.length) {
    editTypes.push('Effects');
  }
  if (layouts?.length) {
    editTypes.push('Layout');
  }

  return editTypes;
};

const trackAIEditsUpdate = (clipId: string, clipData: Clip, editTypes: string[], withSplits: boolean = false): void => {
  const videoSource = getVideoSource(clipData) as 'Event' | 'Recording' | 'Upload';
  const eventName = withSplits ? 'AI_EDITS_APPLIED_WITH_SPLITS' : 'AI_EDITS_APPLIED';
  trackClipUpdate(clipId, eventName, {
    editor_type: 'AI',
    video_source: videoSource,
    edit_type: editTypes
  });
};

const transformElementsFromAIEdits = async (aiEditsData: AIEditsConfigWithSplits['elements'], clipData: Clip) => {
  const { images = [], videos = [], music: audios = [], text = [] } = aiEditsData;
  const fps = getClipFPS(clipData);

  const captionFontLocation =
    clipData.asset_metadata.caption_styles?.font_location || clipData.asset_metadata.font_location;
  const fontFileName = captionFontLocation?.split('/').pop() || 'Poppins-Bold.ttf';
  const fontName = fontFileName.replace(/\.[^/.]+$/, '');
  const calloutFont = {
    name: fontName,
    location: captionFontLocation || getFontFullPath('Poppins-Bold.ttf')
  };

  const videoElements = sanitizeVideoElements(videos, fps);
  const audioElements = await sanitizeAudioElements(audios, fps);
  const imageElements = sanitizeImageElements(images, fps);
  const callOutElements = sanitizeCallOutElements(calloutFont, text || [], fps, clipData.asset_metadata.size);

  return [...videoElements, ...imageElements, ...audioElements, ...callOutElements];
};

const processAIEditsWithSplits = async (
  clipId: string,
  clipData: Clip,
  aiEditsConfig: AIEditsConfigWithSplits
): Promise<void> => {
  const fps = getClipFPS(clipData);
  const compositeSegments = getAllCompositeSegments(clipData);

  const allElements = aiEditsConfig?.elements
    ? await transformElementsFromAIEdits(aiEditsConfig.elements, clipData)
    : undefined;

  const compositeScenes: Array<{ compositeId: string; scenes: Scene[] }> = [];
  const compositeEffects: Array<{ compositeId: string; effects: Effect[] }> = [];
  const compositeDeletes: Array<{ compositeId: string; trims: SanitizedTrim[] }> = [];

  for (const split of aiEditsConfig.splits) {
    const compositeSegment = compositeSegments[split.splitIndex];
    if (!compositeSegment?.segment_metadata) {
      continue;
    }

    const scenes = sanitizeScenesForSplit(split.layouts || [], fps, compositeSegment, clipData);
    const effects = sanitizeEffectsForSplit(split.effects || [], fps, compositeSegment, clipData);
    const trims = sanitizeTrimsForSplit(split.trims || [], fps, compositeSegment, clipData);

    if (scenes.length > 0) {
      compositeScenes.push({ compositeId: compositeSegment.id, scenes });
    }
    if (effects.length > 0) {
      compositeEffects.push({ compositeId: compositeSegment.id, effects });
    }
    if (trims.length > 0) {
      compositeDeletes.push({ compositeId: compositeSegment.id, trims });
    }
  }

  updateAllElementsWithAIEditsAndSplits(clipId, allElements, compositeScenes, compositeEffects, compositeDeletes);

  const { elements } = aiEditsConfig;
  const hasSplitEffects = aiEditsConfig.splits.some((s: AIEditsSplitConfig) => s.effects?.length);
  const hasSplitLayouts = aiEditsConfig.splits.some((s: AIEditsSplitConfig) => s.layouts?.length);

  const editTypes = buildEditTypes(
    elements,
    hasSplitEffects ? [true] : undefined,
    hasSplitLayouts ? [true] : undefined
  );
  trackAIEditsUpdate(clipId, clipData, editTypes, true);
};

export const processAIEditsResponse = async (data: any, clipId: string, clipData: Clip): Promise<void> => {
  const splits = data.ai_edits?.splits as AIEditsSplitConfig[] | undefined;
  let processedData = data;

  if (Array.isArray(splits) && splits.length > 0) {
    const compositeSegments = getAllCompositeSegments(clipData);

    if (compositeSegments.length > 1) {
      await processAIEditsWithSplits(clipId, clipData, data.ai_edits as AIEditsConfigWithSplits);
      return;
    }

    // Splits exist in config but not in player_config: use first split only (backward compat)
    const firstSplit = splits[0] as AIEditsSplitConfig;
    processedData = {
      ai_edits: {
        ...data.ai_edits,
        layouts: firstSplit?.layouts || [],
        effects: firstSplit?.effects || []
      },
      trims: firstSplit?.trims || []
    };
  }

  const { trims = [] } = processedData;
  const aiEditsIsEmpty = !processedData.ai_edits || Object.keys(processedData.ai_edits).length === 0;

  // Case 1: Both ai_edits and trims are empty - return early
  if (aiEditsIsEmpty && (!trims || trims.length === 0)) {
    return;
  }

  // Case 2: ai_edits is empty object but trims exist - skip element sanitization
  // Case 3: ai_edits has content - process normally (even if elements is empty array)
  let allElements: (VideoElement | ImageElement | AudioElement | LottieElement | TextElement)[] | undefined;
  let effects: Effect[];
  let sanitizedScenes: Scene[];

  if (aiEditsIsEmpty) {
    // Case 2: Skip element sanitization, only process trims
    allElements = undefined;
    effects = [];
    sanitizedScenes = [];
  } else {
    // Case 3: Process normally
    const transformed = await transformAIEditsData(processedData, clipData);
    allElements = transformed.allElements;
    effects = transformed.effects;
    sanitizedScenes = transformed.sanitizedScenes;
  }

  const sanitizedTrims = sanitizeTrims(trims || [], clipData);

  const transitionsToNonAudiogramLayout = sanitizedScenes.some(scene => scene.details.layout !== 'AUDIOGRAM');

  updateAllElementsWithAIEdits(
    clipId,
    allElements,
    effects,
    sanitizedScenes,
    sanitizedTrims,
    undefined,
    transitionsToNonAudiogramLayout
  );

  const { elements, effects: aiEffects, layouts } = processedData.ai_edits || {};
  const editTypes = buildEditTypes(elements, aiEffects, layouts);
  trackAIEditsUpdate(clipId, clipData, editTypes, false);
};
